import React, { useState } from "react";
import { Target, Droplets, BarChart3, Sprout } from "lucide-react";
import "../styles/utah-water-ways.css";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import InfoIcon from "@mui/icons-material/Info";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import WaterReport1 from "../components/WaterReports/WaterReport1";
import WaterReport2 from "../components/WaterReports/WaterReport2";
import WaterReport3 from "../components/WaterReports/WaterReport3";
import WaterReport4 from "../components/WaterReports/WaterReport4";
import WaterReport5 from "../components/WaterReports/WaterReport5";

const uwiData = [
  {
    title: "Goal",
    value: "202",
    subtitle: "2030 goal is 202 GPCD",
    legend: "GPCD",
    icon: <Target />,
    color: "orange",
    progress: 100,
  },
  {
    title: "Current Statewide Use",
    value: "207",
    legend: "GPCD",
    subtitle: "Utah used 207 GPCD in 2023",
    icon: <Droplets />,
    color: "blue",
    progress: 80,
  },
  {
    title: "County Progress",
    value: "19/29",
    legend: "Counties above goal",
    subtitle: "10 counties remain behind goal",
    icon: <BarChart3 />,
    color: "brown",
    progress: 50,
  },
  {
    title: "Landscape Conversions",
    value: "3.1",
    legend: "M sq.ft.",
    subtitle: "Turf removed in 2024",
    icon: <Sprout />,
    color: "green",
    progress: 75,
  },
];

const terms = [
  {
    tag: "GPCD",
    title: "Gallons Per Capita Per Day",
    desc: "Measures how many gallons each person uses on average in one day.",
  },
  {
    tag: "AF",
    title: "Acre-feet",
    desc: "A western water-volume unit used for reservoirs, rivers, and conservation buffers.",
  },
  {
    tag: "CFS",
    title: "Cubic feet per second",
    desc: "A flow-rate measure showing how fast water moves through a river channel.",
  },
];

const waterReports = [
  { name: "Per Capita Use" },
  { name: "Water Wise Savings" },
  { name: "Utah Rebates" },
  { name: "Lake Impact" },
  { name: "Usage Measure" },
];

export default function UtahWaterWays() {
  const [expandedCard, setExpandedCard] = useState(null);
  const [activeTab, setActiveTab] = useState("Per Capita Use");

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white text-slate-900">
      <LandingSection
        data={{
          imageName: "utah-water.webp",
          title: "Utah Water",
          visible: "",
          overlay: "linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3))",
        }}
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="uwi-container">
          <h1 className="uwi-title">Key Water Usage Indicators</h1>
          <p className="uwi-subtitle">GPCD - Gallons Per Capita Per Day</p>

          <div className="uwi-grid">
            {uwiData.map((item, i) => {
              const Icon = item.icon;
              return (
                <div className={`uwi-card uwi-card--${item.color}`} key={i}>
                  <div className="uwi-card__iconBox">{item.icon}</div>
                  <span className="uwi-card__label">{item.title}</span>
                  <h2
                    className="uwi-card__value"
                    style={{ color: `${item.color}` }}
                  >
                    {item.value}{" "}
                    <span style={{ fontSize: "20px" }}>{item.legend}</span>
                  </h2>
                  <p className="uwi-card__desc">{item.subtitle}</p>
                  <div className="uwi-card__progressTrack">
                    <div
                      className="uwi-card__progressFill"
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 pt-10 items-center text-center">
          <p className="text-sm md:text-base opacity-80 items-center justify-center flex gap-2">
            <InfoIcon style={{ color: "#3c78d8" }} />{" "}
            <span>Click any report tab to explore its detailed breakdown.</span>
          </p>{" "}
          <div className="tabs-wrapper">
            {/* ◀ Left */}
            <button
              className="tab-scroll-btn left"
              onClick={() =>
                document.getElementById("tabs-scroll-inner")?.scrollBy({
                  left: -200,
                  behavior: "smooth",
                })
              }
            >
              ‹
            </button>

            {/* Tabs */}
            <div id="tabs-scroll-inner" className="tabs-scroll">
              <Tabs
                id="expenditure"
                activeKey={activeTab}
                onSelect={(tab) => setActiveTab(tab)}
                variant="tabs"
                className="browser-tabs"
              >
                {waterReports.map((item) => (
                  <Tab key={item.name} eventKey={item.name} title={item.name} />
                ))}
              </Tabs>
            </div>

            {/* ▶ Right */}
            <button
              className="tab-scroll-btn right"
              onClick={() =>
                document.getElementById("tabs-scroll-inner")?.scrollBy({
                  left: 200,
                  behavior: "smooth",
                })
              }
            >
              ›
            </button>
          </div>
        </section>

        <div className="tab-content max-w-7xl mx-auto pb-4">
          {activeTab === "Per Capita Use" && (
            <div className="rounded-2xl shadow">
              <WaterReport1 />
            </div>
          )}
          {activeTab === "Water Wise Savings" && (
            <div className="rounded-2xl shadow">
              <WaterReport2 />
            </div>
          )}
          {activeTab === "Utah Rebates" && (
            <div className="rounded-2xl shadow">
              <WaterReport3 />
            </div>
          )}
          {activeTab === "Lake Impact" && (
            <div className="rounded-2xl shadow">
              <WaterReport4 />
            </div>
          )}
          {activeTab === "Usage Measure" && (
            <div className="rounded-2xl shadow">
              <WaterReport5 />
            </div>
          )}
        </div>

        <div className="uwi-sections-wrapper">
          {/* 🔹 DEFINITIONS */}
          <section className="uwi-section">
            <div className="uwi-section__header">
              <span className="uwi-section__label">
                Quick Refernce: Definitions
              </span>
              <h2>Terms used throughout the report</h2>
              <p>
                The site already uses a consistent global font. What readers
                need here is term clarity, so these definitions reduce friction
                before they reach the charts and tables.
              </p>
            </div>

            <div className="uwi-terms-grid">
              {terms.map((item, i) => (
                <div className="uwi-term-card" key={i}>
                  <div className="uwi-term-card__tag">{item.tag}</div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="uwi-section uwi-section--compact">
            <div className="uwi-section__header uwi-section__header--tight">
              <span className="uwi-section__label">
                Quick Reference: Sources
              </span>
              <h2>Source links</h2>
              <p>Report links used to assemble the Utah Water page.</p>
            </div>

            <div className="uwi-source-list uwi-source-list--compact">
              {[
                "https://water.utah.gov/",
                "https://waterrights.utah.gov/",
                "https://www.utahfoundation.org/uploads/rr723.pdf",
                "https://slowtheflow.org/rebates/",
                "https://waterdesk.org/2025/12/utah-has-county-by-county-water-goals-which-ones-are-hitting-them/",
              ].map((link, i) => (
                <a
                  key={i}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="uwi-source-item--compact"
                >
                  <span className="uwi-source-dot" />
                  <span className="uwi-source-text">{link}</span>
                </a>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
